import { useRefFn } from '@fullstory/react-hooks';
import { useDebugValue, useRef, useState } from 'react';
import { BehaviorSubject, isObservable, Observable, Subject } from 'rxjs';
import type { useSubscription } from '../useSubscription';
import { getEmptySubject } from './getEmptySubject';

type InitFn<TState, TInput> = (
  input$: Observable<TInput>,
  initialState?: TState,
) => Observable<TState>;
type InitialState<T> = T | (() => T);

export function useObservableStateInternal<TState, TInput = TState>(
  useSubscriptionFn: typeof useSubscription,
  state$OrInit: Observable<TState> | InitFn<TState, TInput>,
  initialState?: InitialState<TState>,
): TState | undefined | [TState | undefined, (input: TInput) => void] {
  // Initialize the local state
  const [state, setState] = useState<TState | undefined>(() => {
    if (
      state$OrInit instanceof BehaviorSubject ||
      (state$OrInit as BehaviorSubject<TState>).value !== undefined
    ) {
      return (state$OrInit as BehaviorSubject<TState>).value;
    }

    if (typeof initialState === 'function') {
      return (initialState as () => TState)();
    }

    return initialState;
  });

  const input$Ref = useRefFn<Subject<TInput>>(getEmptySubject);
  const callback = useRef((state: TInput) => input$Ref.current.next(state)).current;

  const initStateRef = () =>
    isObservable(state$OrInit) ? state$OrInit : state$OrInit(input$Ref.current, state);
  const state$ = useRefFn(initStateRef).current;

  useSubscriptionFn(state$, setState);
  useDebugValue(state);

  return isObservable(state$OrInit) ? state : [state, callback];
}
